/*
 * This is the header.js entrypoint consisting of critical JS code, all bundled.
 * Whenever you need to introduce something which should run before the main Vue app hydrated, put it here.
 *
 * NB: the current boot sequence:
 * 0) booting absolutely critical sections (Sentry, splits, etc.)
 * 1) unconditionally exposing 1st party library code (YouTube events bus, etc.)
 * 2) monkey-patching `window._vwo_evq.push` to hook into redirect event
 * 3) setting up VWO init code (not calling it yet)
 * 4) adding OneTrust consent listener
 *    5) [in OneTrust.consentGiven callback] unconditionally initiating VWO
 * 6) [in parallel with 4)] pre-pushing userType GA event, initiating GTM, starting VWO result polling
 *    7) [in VWO polling complete, if no redirect] filling initial dataLayer events (BOGO, businessGeo)
 * 8) [in parallel with 4)] unconditionally initiating Matomo and pushing the pageview
*/

import { Logger } from '../../utils/logger';
import { getGeolocation } from './boot-geolocation';
import { initGTM } from './boot-gtm';
import { injectVWOCode } from './boot-inject-vwo-code.js';
import { installOneTrustHook } from './boot-install-onetrust-hook';
import { initMatomo } from './boot-matomo';
import { increaseNavvaticCounter } from './boot-navvatic-counter';
import { sendBogoEvent } from './boot-send-bogo';
import { sendBusinessLocationEvent } from './boot-send-business-location';
import { initSplits } from './boot-splits';
import { monkeyPatchVWO } from './boot-vwo';
import { initSentry } from './utils-sentry';
import { getVWOOutcome } from './utils-wait-for-vwo-outcome';
import { sendWebVitals } from './utils-web-vitals';
import { YoutubePlayerReadyBus } from './utils-youtube-player-ready-bus';

function safeBootSection(fn: () => void, logName: string) {
    try {
        fn();
    } catch (e) {
        Logger.error(`Failed to boot ${logName}`, e);
    }
}

async function safeBootSectionAsync(fn: () => Promise<void>, logName: string) {
    try {
        await fn();
    } catch (e) {
        Logger.error(`Failed to boot ${logName}`, e);
    }
}

export async function bootHeader() {
    safeBootSection(initSplits, 'splits');
    safeBootSection(sendWebVitals, 'sendWebVitals');
    safeBootSection(initSentry, 'initSentry');
    safeBootSection(increaseNavvaticCounter, 'increaseNavvaticCounter');

    window.youtubePlayerReadyBus = new YoutubePlayerReadyBus();

    safeBootSection(monkeyPatchVWO, 'monkeyPatchVWO');
    safeBootSection(injectVWOCode, 'injectVWOCode');
    // Once again: no initVWO here, because we need to wait for OT consent
    safeBootSection(installOneTrustHook, 'installOtHook');
    safeBootSection(initGTM, 'initGTM');
    safeBootSectionAsync(initMatomo, 'initMatomo');

    // Code which requires VWO decision making goes here (only some GA events for now)
    const vwoOutcome = await getVWOOutcome();
    if (vwoOutcome !== 'redirect') {
        await getGeolocation();
        sendBusinessLocationEvent();
        sendBogoEvent();
    }
}
